import { z } from "zod";

/**
 * Casella email del tenant (IMAP) — Impostazioni → Casella email.
 *
 * Serve agli agenti (chiavi API con scope mailbox:*) per cercare le email e
 * salvare BOZZE nella cartella Bozze della casella: l'invio non esiste come
 * operazione, le bozze le invia una persona dal proprio client di posta.
 */

/** Alias di cartella risolti con SPECIAL-USE (o con il nome configurato). */
export const MAILBOX_FOLDER_ALIASES = ["inbox", "sent", "drafts"] as const;
export type MailboxFolderAlias = (typeof MAILBOX_FOLDER_ALIASES)[number];

/** Testo su una sola riga: niente a capo nelle intestazioni (header injection). */
const singleLine = (max: number) =>
  z
    .string()
    .trim()
    .min(1)
    .max(max)
    .transform((v) => v.replace(/[\r\n]+/g, " "));

const optionalFolder = z
  .string()
  .trim()
  .max(200)
  .optional()
  .or(z.literal("").transform(() => undefined));

/**
 * Salvataggio della configurazione. `imapPassword` assente = mantieni quella
 * già salvata (la password non viene mai rimandata al client).
 */
export const updateTenantMailboxSchema = z.object({
  fromName: singleLine(120),
  fromEmail: z.string().trim().toLowerCase().email().max(254),
  imapHost: z.string().trim().min(1).max(253),
  imapPort: z.coerce.number().int().min(1).max(65535).default(993),
  imapSecure: z.boolean().default(true),
  imapUser: z.string().trim().min(1).max(254),
  imapPassword: z.string().min(1).max(1024).optional(),
  draftsFolder: optionalFolder,
  sentFolder: optionalFolder,
});
export type UpdateTenantMailboxInput = z.infer<typeof updateTenantMailboxSchema>;

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/, "data ISO (AAAA-MM-GG)");

/** Cartella: alias (inbox | sent | drafts) oppure percorso IMAP esatto. */
const folderParam = z.string().trim().min(1).max(200).default("inbox");

export const mailboxSearchQuerySchema = z.object({
  folder: folderParam,
  /** Indirizzo completo o dominio (es. @azienda.it): match sul campo From. */
  from: z.string().trim().max(254).optional(),
  to: z.string().trim().max(254).optional(),
  subject: z.string().trim().max(300).optional(),
  /** Testo libero in intestazioni e corpo. */
  text: z.string().trim().max(300).optional(),
  since: isoDate.optional(),
  before: isoDate.optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type MailboxSearchQuery = z.infer<typeof mailboxSearchQuerySchema>;

export const mailboxMessageQuerySchema = z.object({
  folder: folderParam,
  uid: z.coerce.number().int().min(1),
});
export type MailboxMessageQuery = z.infer<typeof mailboxMessageQuerySchema>;

const address = z.string().trim().toLowerCase().email().max(254);

export const createMailboxDraftSchema = z.object({
  to: z.array(address).min(1).max(20),
  cc: z.array(address).max(20).optional(),
  subject: singleLine(300),
  /** Testo semplice: il server genera anche una versione HTML minimale. */
  bodyText: z.string().min(1).max(50_000),
  bodyHtml: z.string().max(200_000).optional(),
});
export type CreateMailboxDraftInput = z.infer<typeof createMailboxDraftSchema>;

export const createMailboxReplyDraftSchema = z.object({
  folder: folderParam,
  uid: z.number().int().min(1),
  bodyText: z.string().min(1).max(50_000),
  /** true = risponde a mittente e a tutti i destinatari (tranne la casella stessa). */
  replyAll: z.boolean().default(false),
});
export type CreateMailboxReplyDraftInput = z.infer<typeof createMailboxReplyDraftSchema>;

/** Riassunto di un messaggio in un elenco di ricerca. */
export interface MailboxMessageSummary {
  uid: number;
  folder: string;
  messageId: string | null;
  inReplyTo: string | null;
  date: string | null;
  from: string[];
  to: string[];
  cc: string[];
  subject: string;
  seen: boolean;
  size: number | null;
}

export interface MailboxMessageDetail extends MailboxMessageSummary {
  references: string[];
  replyTo: string[];
  text: string;
  textTruncated: boolean;
  attachments: Array<{ filename: string | null; contentType: string; size: number }>;
}

export interface MailboxDraftResult {
  folder: string;
  uid: number | null;
  messageId: string;
  to: string[];
  cc: string[];
  subject: string;
}
